import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, FormLabel, Row } from 'react-bootstrap';
import { Spinner2 } from 'components';
import { Select, IOption } from 'components/Select';
import { useQueryBuilder, FilterRule } from 'components/Filter/useQueryBuilder';
import {
  IParts,
  useGetPartsQuery,
  useRedoPartMutation,
} from 'thunks/RTKQuery/parts';
import { useGetMillsQuery } from 'thunks/RTKQuery/mills';
import ManagerLayout from 'components/Layout/Manager/ManagerLayout';
import { PartsView, ISortBy } from 'pages/Shared/Parts';
import { getPageSize } from 'utils/common';
import { isFetchBaseQueryError } from 'utils/helpers';
import { toastSlice } from 'slices';
import { useAppDispatch } from 'store/hooks';

const defaultSortBy: ISortBy = { dataField: 'name', order: 'asc' };

export const ManagerPartsView = () => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(+getPageSize());
  const [sortBy, setSortBy] = useState<ISortBy>(defaultSortBy);
  const [parts, setParts] = useState<IParts[]>([]);
  const [filters, setFilters] = useState<FilterRule[]>([]);
  const [selectedMillId, setSelectedMillId] = useState<number | null>(null);
  const selectedMillIdRef = useRef(selectedMillId);
  selectedMillIdRef.current = selectedMillId;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [errorString, setErrorString] = useState('');

  const { params } = useQueryBuilder(filters);

  const { data: mills, isFetching: isLoadingMills } = useGetMillsQuery();

  const millOptions: IOption[] = useMemo(
    () =>
      (mills || [])
        .slice()
        .sort((a, b) => a.id - b.id)
        .map((mill) => ({
          label: `${mill.id} - ${mill.name}`,
          value: mill.id,
        })),
    [mills]
  );

  const { data, isFetching } = useGetPartsQuery(
    {
      pageSize,
      pageNumber: page,
      sort: `${sortBy.dataField}:${sortBy.order}`,
      millId: selectedMillId,
      ...params,
    },
    { skip: !selectedMillId }
  );

  useEffect(() => {
    if (millOptions.length && !selectedMillId) {
      setSelectedMillId(millOptions[0].value);
    }
  }, [millOptions]);

  useEffect(() => {
    if (data?.data) {
      setParts(data.data);
    }
  }, [data]);

  useEffect(() => {
    setPage(1); // Reset to first page whenever filters or mill change
  }, [filters, selectedMillId]);

  const [
    redoPart,
    { isLoading: redoIsLoading, status: redoStatus, error: redoError },
  ] = useRedoPartMutation();
  const handleRedo = (id: number) => {
    redoPart({ id, millId: selectedMillIdRef.current });
  };

  useEffect(() => {
    if (redoStatus === 'fulfilled' && !redoError) {
      dispatch(
        toastSlice.actions.show({
          title: t('partsView.redoSuccess'),
          message: t('partsView.redoSuccessMessage'),
        })
      );
    }
  }, [redoStatus]);

  const handleMillChange = (option: IOption) => {
    setSelectedMillId(option.value);
  };

  useEffect(() => {
    if (redoError && isFetchBaseQueryError(redoError)) {
      const msg = redoError as any;
      setErrorString(`${msg.data.message.errors}`);
    }
  }, [redoError]);

  useEffect(() => {
    if (errorString !== '') {
      dispatch(
        toastSlice.actions.show({
          title: t('contract.error'),
          message: errorString,
        })
      );
    }
    setErrorString('');
  }, [errorString]);

  return (
    <ManagerLayout>
      <div className="d-flex flex-column w-100">
        <Spinner2 show={isLoadingMills || isFetching || redoIsLoading} />
        <Row>
          <Col>
            <h1 className="fs-44 mb-10">{t('partsView.heading')}</h1>
          </Col>
        </Row>

        <Row className="mt-1 mb-24">
          <Col className="d-flex justify-content-end">
            <div className="select-wrapper mb-24 mb-md-0">
              <FormLabel>Select Mill</FormLabel>
              <div className="mt-2">
                <Select
                  options={millOptions}
                  value={millOptions.find(
                    (option) => option.value === selectedMillId
                  )}
                  onChange={handleMillChange}
                />
              </div>
            </div>
          </Col>
        </Row>

        <PartsView
          parts={parts}
          count={data?.count || 0}
          page={page}
          sizePerPage={pageSize}
          setPage={setPage}
          setSortBy={setSortBy}
          sortBy={sortBy}
          filters={filters}
          setFilters={setFilters}
          redoPart={handleRedo}
        />
      </div>
    </ManagerLayout>
  );
};

export default ManagerPartsView;
